import { FeedbackResultData } from '@Quarantine/UserReportedEmails/type';
import {
  Alert,
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Spinner,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { DeployIcon } from '@tonic-one/react-icons';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Trans } from 'react-i18next';
import riskAIApi from '@Apis/riskAI';
import { useNavigateWithHandler } from '@Utils/hooks';

type CustomRuleKey =
  | 'urls'
  | 'urlDomains'
  | 'senderAddresses'
  | 'senderDomains';

interface RemediationSectionProps {
  isHighConfidence: boolean;
  orgId: string;
  customRule?: FeedbackResultData['customRule'];
  caseState?: FeedbackResultData['caseState'];
  processingState?: FeedbackResultData['processingState'];
  remediationError?: boolean;
}

export const RemediationSection = ({
  isHighConfidence,
  orgId,
  customRule,
  caseState,
  processingState,
  remediationError,
}: RemediationSectionProps) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [alreadyMonitoredItems, setAlreadyMonitoredItems] = useState<
    Map<CustomRuleKey, Set<string>>
  >(new Map());
  const navigate = useNavigateWithHandler();

  // Query to fetch email loop expressions and check which items are already monitored
  const { data: expressions, isLoading: isLoadingExpressions } = useQuery({
    queryKey: ['emailLoopExpressions', orgId],
    queryFn: async () => {
      const response = await riskAIApi.getExpressions(orgId);
      // Filter for email_loop markers
      return response.data.filter(
        (exp: any) => exp.raiExpMarkers === 'email_loop'
      );
    },
    enabled: !isHighConfidence && !!customRule,
  });

  // Parse expressions to extract monitored values
  useEffect(() => {
    if (!expressions) return;

    const monitoredMap = new Map<CustomRuleKey, Set<string>>();

    expressions.forEach((exp: any) => {
      try {
        const content = JSON.parse(exp.raiExpContent);
        const valueStrings = content.conditions?.[0]?.valueStrings || [];

        // Map expression names to custom rule keys
        if (exp.raiExpName === 'Monitored Senders from User-Reported Emails') {
          const existing = monitoredMap.get('senderAddresses') || new Set();
          valueStrings.forEach((value: string) => existing.add(value));
          monitoredMap.set('senderAddresses', existing);

          const existingDomains =
            monitoredMap.get('senderDomains') || new Set();
          valueStrings.forEach((value: string) => existingDomains.add(value));
          monitoredMap.set('senderDomains', existingDomains);
        } else if (
          exp.raiExpName === 'Monitored URLs from User-Reported Emails'
        ) {
          const existing = monitoredMap.get('urls') || new Set();
          valueStrings.forEach((value: string) => existing.add(value));
          monitoredMap.set('urls', existing);
        } else if (
          exp.raiExpName === 'Monitored URL Domains from User-Reported Emails'
        ) {
          const existing = monitoredMap.get('urlDomains') || new Set();
          valueStrings.forEach((value: string) => existing.add(value));
          monitoredMap.set('urlDomains', existing);
        }
      } catch (error) {
        console.error('Error parsing expression content:', error);
      }
    });

    setAlreadyMonitoredItems(monitoredMap);
  }, [expressions]);

  const handleNavigateToSignalList = useCallback(
    (ruleType: CustomRuleKey) => {
      // Navigate to the detection signal list page with a query parameter to highlight the relevant signal
      const signalNames = {
        urls: 'Monitored URLs from User-Reported Emails',
        urlDomains: 'Monitored URL Domains from User-Reported Emails',
        senderAddresses: 'Monitored Senders from User-Reported Emails',
        senderDomains: 'Monitored Senders from User-Reported Emails',
      };

      const signalName = signalNames[ruleType];
      const url = `/policy/global-settings/detection-signal-list?highlight=${encodeURIComponent(signalName)}`;

      navigate(url);
    },
    [navigate]
  );

  const addToMonitoredListMutation = useMutation({
    mutationFn: async ({
      ruleType,
      values,
    }: {
      ruleType: CustomRuleKey;
      values: string[];
    }) => {
      const data = { [ruleType]: values };
      const response = await riskAIApi.addEmailLoopMonitoring(orgId, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { ruleType } = variables;

      if (data.success) {
        window.showNotification({
          severity: 'success',
          titleMsg: t('operations.euq.remediation.manual'),
          bodyMsg: t(
            'operations.euq.remediation.customRule.takeAction.success'
          ),
        });
      }

      setAddedItems((prev) => new Set(prev).add(ruleType));
    },
    onError: (error: any, { ruleType }) => {
      console.error('Error adding to monitored list:', error);

      // Handle different error types from the backend
      const errorMessage =
        error?.data?.data?.code || error?.data?.code || error?.message;

      if (errorMessage === 'EXCEEDS_MAX_ENTRIES') {
        window.showNotification({
          severity: 'error',
          titleMsg: t('operations.euq.remediation.manual'),
          bodyMsg: (
            <Text>
              <Trans
                i18nKey={`operations.euq.remediation.customRule.takeAction.details.${ruleType}.error`}
                values={{
                  ruleType: t(
                    `operations.euq.remediation.customRule.takeAction.details.${ruleType}.error2`
                  ),
                }}
              >
                placeholder
                <Link
                  display="inline"
                  color="blue:50"
                  onClick={() => handleNavigateToSignalList(ruleType)}
                  target="_blank"
                >
                  placeholder
                </Link>
                placeholder
              </Trans>
            </Text>
          ),
        });
      } else if (errorMessage === 'DUPLICATE_EMAIL_LOOP_RULE') {
        window.showNotification({
          severity: 'success',
          titleMsg: t('operations.euq.remediation.manual'),
          bodyMsg: t(
            'operations.euq.remediation.customRule.takeAction.details.success.alreadyExists'
          ),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('operations.euq.remediation.manual'),
          bodyMsg: t('common.apiException.internalError'),
        });
      }
    },
  });

  const renderLowConfidenceAnalysis = useCallback(() => {
    if (isHighConfidence) return null;

    const customRuleEntries = Object.entries(customRule ?? {}).filter(
      ([, values]) => values && values.length > 0
    );

    if (customRuleEntries.length === 0) {
      return (
        <Flex
          minHeight={150}
          flex={1}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Text textAlign="center" color={colorStyle.color.secondary}>
            {t('operations.euq.remediation.customRule.notRequired')}
          </Text>
        </Flex>
      );
    }

    if (isLoadingExpressions) {
      return (
        <Flex
          minHeight={150}
          flex={1}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="md" />
        </Flex>
      );
    }

    return (
      <Stack gap="2x">
        {customRuleEntries.map(([key, values]) => {
          const ruleKey = key as CustomRuleKey;
          const isAdded = addedItems.has(ruleKey);
          const isLoading =
            addToMonitoredListMutation.isPending &&
            addToMonitoredListMutation.variables?.ruleType === ruleKey;

          // Check which items are already monitored
          const monitoredSet = alreadyMonitoredItems.get(ruleKey) || new Set();
          const itemsAlreadyMonitored = (values || []).filter((value) =>
            monitoredSet.has(value)
          );
          const itemsNotMonitored = (values || []).filter(
            (value) => !monitoredSet.has(value)
          );
          const allItemsMonitored =
            itemsAlreadyMonitored.length === values?.length &&
            values.length > 0;

          return (
            <Stack gap="4x" key={key}>
              <Flex gap="1x" flexWrap="wrap">
                <Text color={colorStyle.color.secondary}>
                  {t(`operations.euq.remediation.customRule.${key}`)}:
                </Text>
                <Text>{values?.join(', ')}</Text>
              </Flex>
              {isAdded ? (
                <Text color={colorStyle.color.secondary} width="max-content">
                  {t('operations.euq.remediation.customRule.addedToList')}
                </Text>
              ) : allItemsMonitored ? (
                <Text color={colorStyle.color.secondary} width="max-content">
                  {t('operations.euq.remediation.customRule.addedToList')}
                </Text>
              ) : (
                <Button
                  width="max-content"
                  disabled={isLoading}
                  onClick={() =>
                    addToMonitoredListMutation.mutate({
                      ruleType: ruleKey,
                      values: itemsNotMonitored,
                    })
                  }
                  gap="2x"
                >
                  {t(`operations.euq.remediation.customRule.takeAction.${key}`)}
                  {isLoading && <Spinner size="xs" lineColor="inherit" />}
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>
    );
  }, [
    colorStyle.color.secondary,
    customRule,
    isHighConfidence,
    addedItems,
    addToMonitoredListMutation,
    alreadyMonitoredItems,
    isLoadingExpressions,
  ]);

  const renderHighConfidenceStatus = useCallback(() => {
    if (!caseState) return null;
    let status = null;
    if (caseState === 'reasoning_ready' && processingState !== 'error') {
      status = t('operations.euq.remediation.status.inProgress');
    } else if (caseState === 'solution_ready') {
      status = t('operations.euq.remediation.status.completed');
    }
    return (
      <Flex gap="1x" flexWrap="wrap">
        <Text color={colorStyle.color.secondary}>
          {t(`operations.euq.remediation.status`)}:
        </Text>
        <Text>{!status ? '-' : status}</Text>
      </Flex>
    );
  }, [caseState, colorStyle.color.secondary, processingState]);

  return (
    <Box px="4x" pb="12x">
      <Flex alignItems="center" gap="2x">
        <Icon as={DeployIcon} size="4x" />
        <Text size="lg" fontWeight={500}>
          {isHighConfidence
            ? t('operations.euq.remediation.automatic')
            : t('operations.euq.remediation.manual')}
        </Text>
      </Flex>
      <Stack gap="4x" mt="2x">
        <Text
          color={colorStyle.color.secondary}
          dangerouslySetInnerHTML={{
            __html: isHighConfidence
              ? t('operations.euq.remediation.details.automatic.description')
              : t('operations.euq.remediation.details.manual.description'),
          }}
        />
        {remediationError && (
          <Stack gap="4x">
            <Alert severity="warning" variant="outline">
              {t('operations.euq.remediation.error')}
            </Alert>
            <Flex
              minHeight={150}
              flex={1}
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Text textAlign="center" color={colorStyle.color.secondary}>
                {t('common.noData')}
              </Text>
            </Flex>
          </Stack>
        )}
        {!remediationError &&
          (isHighConfidence
            ? renderHighConfidenceStatus()
            : renderLowConfidenceAnalysis())}
      </Stack>
    </Box>
  );
};
